// PartnerPairingBanner -- shows above the dashboard for any org that
// is younger than 72 hours and does not yet have a partners.referrals
// row. Dismissible per-org via localStorage.
//
// Privacy: the banner only renders if the operator EXPLICITLY navigates
// or if they have the active org context loaded. It performs ONE
// HEAD-style query against `partners.referrals` for the active org.
// Because partners.referrals.RLS denies authenticated read except for
// the referrer themselves, the operator can't see the row content
// either -- they just see whether `count(*)` is zero or non-zero,
// which is what we want for the dismiss logic.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getOrgReferralPairing, supabase } from '@pc/db';
import { Button } from '@pc/ui';
import { useActiveContext } from '../auth/ActiveContextProvider';

const DISMISS_KEY_PREFIX = 'pc.pair_banner_dismissed.';
const WINDOW_HOURS = 72;

export function PartnerPairingBanner() {
  const { activeOrgId } = useActiveContext();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!activeOrgId) return;
    try {
      const v = localStorage.getItem(DISMISS_KEY_PREFIX + activeOrgId);
      setDismissed(v === '1');
    } catch {
      // localStorage unavailable; leave banner visible
    }
  }, [activeOrgId]);

  const orgQ = useQuery({
    queryKey: ['pair-banner-org', activeOrgId],
    queryFn: async () => {
      if (!activeOrgId) return null;
      const { data, error } = await supabase()
        .schema('core')
        .from('organizations')
        .select('id, created_at')
        .eq('id', activeOrgId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: Boolean(activeOrgId),
    staleTime: 60_000,
  });

  const withinWindow = useMemo(() => {
    if (!orgQ.data?.created_at) return false;
    const created = new Date(orgQ.data.created_at).getTime();
    const ageHours = (Date.now() - created) / (1000 * 60 * 60);
    return ageHours < WINDOW_HOURS;
  }, [orgQ.data?.created_at]);

  // Skip the referral existence check if we're already past the window.
  const referralQ = useQuery({
    queryKey: ['pair-banner-referral', activeOrgId],
    queryFn: () => getOrgReferralPairing(activeOrgId!),
    enabled: Boolean(activeOrgId) && withinWindow,
    staleTime: 60_000,
  });

  const hasReferral = referralQ.data?.paired === true;
  const hoursRemaining = useMemo(() => {
    if (!orgQ.data?.created_at) return 0;
    const created = new Date(orgQ.data.created_at).getTime();
    const remaining = WINDOW_HOURS - (Date.now() - created) / (1000 * 60 * 60);
    return Math.max(0, Math.ceil(remaining));
  }, [orgQ.data?.created_at]);

  if (!activeOrgId || dismissed || !withinWindow || hasReferral) return null;

  return (
    <div className="mb-6 border border-rule bg-paper-2 px-4 py-3 flex items-center gap-4">
      <div className="flex-1 text-sm">
        <p className="text-ink">
          Were you referred by a PeptideClients partner?
        </p>
        <p className="text-muted mt-0.5">
          Enter their referral code within the next{' '}
          <strong>{hoursRemaining}h</strong> so they get credited as your
          account manager.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Link to="/pair" className="shrink-0">
          <Button size="sm">Enter code</Button>
        </Link>
        <button
          type="button"
          onClick={() => {
            try {
              localStorage.setItem(DISMISS_KEY_PREFIX + activeOrgId, '1');
            } catch {
              // ignore
            }
            setDismissed(true);
          }}
          className="text-sm text-muted underline px-2 py-1"
          aria-label="Dismiss"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
